import { showCauseOptions } from '@givebacks/api/core';
import { type Checkout } from '@givebacks/api/store';
import { type QueryClient } from '@tanstack/react-query';

import type { CheckoutOrganizationNamesById } from './pendoCheckoutCompleted';

type CheckoutCause = {
  name?: string | null;
};

/**
 * Builds a map of `cause_id -> cause name` for carts in a checkout.
 * Uses `ensureQueryData` so names are loaded from cache or fetched on miss.
 * This helper is best-effort: individual cause query failures are ignored.
 */
const getCheckoutOrganizationNamesFromCache = async (
  checkout: Checkout,
  queryClient: QueryClient
): Promise<CheckoutOrganizationNamesById> => {
  const causeIds = Array.from(
    new Set(
      checkout.carts
        .map((cart) => cart.cause_id)
        .filter((causeId): causeId is string => Boolean(causeId))
    )
  );

  const organizationNamesById: CheckoutOrganizationNamesById = {};

  await Promise.all(
    causeIds.map(async (causeId) => {
      try {
        const cachedCause = await queryClient.ensureQueryData(showCauseOptions({ uuid: causeId }));
        const causeName = (cachedCause as CheckoutCause | undefined)?.name?.trim();

        if (causeName) {
          organizationNamesById[causeId] = causeName;
        }
      } catch {
        // no-op: tracking enrichment should never fail checkout tracking
      }
    })
  );

  return organizationNamesById;
};

export { getCheckoutOrganizationNamesFromCache };
